'use client';

import { useEffect } from 'react';

import {
  getBrowserPreferredLocaleClient,
  getLocaleCookieClient,
  setLocaleCookieClient
} from '@website/utils/localeCookie';

/**
 * LocaleSync Component
 *
 * This component runs on every page load to ensure localStorage and cookies are in sync.
 * It's necessary because:
 * 1. localStorage ALWAYS persists (primary source of truth)
 * 2. Cookies can expire or be deleted
 * 3. Middleware only has access to cookies
 *
 * On first visit (no stored preference):
 * - Detects browser language using navigator.language
 * - Applies French-priority logic (if French is in browser languages, use French)
 * - Saves detected language to localStorage + cookie
 *
 * On subsequent visits:
 * - Uses stored preference from localStorage
 * - Syncs to cookie (in case it expired)
 */
export function LocaleSync() {
  useEffect(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;

    try {
      // Get the stored locale preference
      const storedLocale = getLocaleCookieClient();

      if (!storedLocale) {
        // First visit: detect from browser and save
        const browserLocale = getBrowserPreferredLocaleClient();
        setLocaleCookieClient(browserLocale);
        console.log('[LocaleSync] First visit - detected and saved browser locale:', browserLocale);
      } else {
        // Ensure cookie is synced (in case it expired)
        setLocaleCookieClient(storedLocale);
        console.log('[LocaleSync] Synced stored locale:', storedLocale);
      }
    } catch (error) {
      console.error('[LocaleSync] Error syncing locale:', error);
    }
  }, []); // Run once on mount

  return null; // This component doesn't render anything
}
